// drag-order.ts — Câblage du réordonnancement par glisser-déposer.
//
// Le motif (dragstart/dragover/dragleave/drop + poignée ⠿ + classe
// `.drag-over`) existait déjà, recopié dans sept modules. Il est factorisé ici
// pour les écrans de cette branche ; les pages historiques gardent leur copie
// tant qu'on ne les rouvre pas (règle : pas de refacto du code voisin).
//
// Le calcul du nouvel ordre vit dans `domain/drag-order.ts` (testable sans DOM).

import { moveTo } from '../domain/drag-order.js';

export interface DragOrderOptions {
  /** Conteneur des lignes (`<tbody>` ou `<ul>`). */
  container: HTMLElement;
  /** Sélecteur des éléments déplaçables, relatif au conteneur. */
  itemSelector: string;
  /** Clé `dataset` portant l'identifiant numérique (ex. `guideId`). */
  idKey: string;
  /** Appelé avec le nouvel ordre complet, après un dépôt effectif. */
  onReorder: (ids: number[]) => void;
}

const OVER_CLASS = 'drag-over';

function idsOf(container: HTMLElement, selector: string, idKey: string): number[] {
  return [...container.querySelectorAll<HTMLElement>(selector)].map((el) =>
    Number(el.dataset[idKey] || 0),
  );
}

/**
 * Rend les éléments du conteneur réordonnables au glisser-déposer.
 *
 * À rappeler après chaque re-rendu du conteneur : les écouteurs sont posés sur
 * le conteneur lui-même, donc remplacer son `innerHTML` les emporte.
 */
export function initDragOrder(opts: DragOrderOptions): void {
  const { container, itemSelector, idKey, onReorder } = opts;
  let src: HTMLElement | null = null;

  const clearOver = (): void =>
    container
      .querySelectorAll<HTMLElement>(itemSelector)
      .forEach((el) => el.classList.remove(OVER_CLASS));

  container.addEventListener('dragstart', (e: DragEvent) => {
    src = (e.target as HTMLElement | null)?.closest<HTMLElement>(itemSelector) ?? null;
    if (!src) return;
    src.style.opacity = '0.4';
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', src.dataset[idKey] || '');
    }
  });

  container.addEventListener('dragend', () => {
    if (src) src.style.opacity = '';
    clearOver();
    src = null;
  });

  container.addEventListener('dragover', (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(itemSelector);
    clearOver();
    if (target && target !== src) target.classList.add(OVER_CLASS);
  });

  container.addEventListener('dragleave', (e: DragEvent) => {
    (e.target as HTMLElement | null)
      ?.closest<HTMLElement>(itemSelector)
      ?.classList.remove(OVER_CLASS);
  });

  container.addEventListener('drop', (e: DragEvent) => {
    e.preventDefault();
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(itemSelector);
    clearOver();
    if (!target || !src || target === src) return;
    const srcId = Number(src.dataset[idKey] || 0);
    const targetId = Number(target.dataset[idKey] || 0);
    src.style.opacity = '';
    src = null;
    onReorder(moveTo(idsOf(container, itemSelector, idKey), srcId, targetId));
  });
}

/** Cellule de poignée ⠿, dimensionnée comme celles des tables existantes. */
export function dragHandleCell(label: string): string {
  return `<td style="width:32px;text-align:center"><span class="drag-handle" role="button" aria-label="${esc(
    label,
  )}">⠿</span></td>`;
}
